import { formatHours } from "@activecollab/components";
import { createSelector } from "reselect";
import { getAllProjects } from "~/store/selectors/selector.projects";
import { getAllTasks } from "~/store/selectors/selector.tasks";
import { makeCanViewProjectElement } from "~/store/selectors/selector.users";
import { descSortCompare, roundByIncrements, taskClass, userClass } from "~/utils/utils.jsx";
import { getRawConfigOption } from "./selector.configOptions.jsx";

export const getAllStopwatches = (state) => state.stopwatches;

export const getAllStopwatchesArray = createSelector(getAllStopwatches, (stopwatches) => Object.values(stopwatches));

export const getAllAssignedStopwatchesArray = createSelector(getAllStopwatches, (stopwatches) => {
  return Object.values(stopwatches).filter((stopwatch) => stopwatch.parent_type !== "User");
});

export const getUnassignedStopwatch = createSelector(getAllStopwatches, (stopwatches) => {
  return Object.values(stopwatches).find((stopwatch) => stopwatch.parent_type === "User");
});

export const getAllStopwatchesSortedByCreatedDate = createSelector(getAllAssignedStopwatchesArray, (stopwatches) =>
  stopwatches.sort((a, b) => descSortCompare(a.created_on, b.created_on))
);

export const getStopwatchById = (state, id) => state.stopwatches[`${id}`];

const getStopwatchesByType = createSelector(
  getAllStopwatchesArray,
  (state, parent_type) => parent_type,
  (stopwatches, parent_type) => stopwatches.filter((stopwatch) => stopwatch.parent_type === parent_type)
);

export const getStopwatchByParentTypeAndParentId = createSelector(
  getStopwatchesByType,
  (state, parent_type, parent_id) => parent_id,
  (stopwatchesForParentType, parent_id) =>
    stopwatchesForParentType.find((stopwatch) => stopwatch.parent_id === parent_id)
);

export const getStopwatchValue = createSelector(
  getStopwatchById,
  (state) => getRawConfigOption(state, "rounding_interval"),
  (state) => getRawConfigOption(state, "minimal_time_entry"),
  (state) => getRawConfigOption(state, "rounding_enabled"),
  (stopwatch, roundingInterval, minimalTimeEntry, roundingEnabled) => {
    if (stopwatch) {
      let elapsedMins = Math.floor(stopwatch.elapsed / 60);
      if (elapsedMins > 5999) elapsedMins = 5999;
      const roundingStep = roundingEnabled ? roundingInterval : 1;

      let roundedValue = roundByIncrements(elapsedMins, roundingStep);
      if (roundingEnabled && roundedValue < minimalTimeEntry) {
        roundedValue = minimalTimeEntry;
      }

      return roundedValue / 60;
    }
    return 0;
  }
);

export const getHoursFormattedForStopwatch = createSelector(getStopwatchValue, (value) => {
  return value > 0 ? formatHours(value) : "";
});

export const getActiveStopwatch = createSelector(getAllStopwatchesArray, (allStopwatches) => {
  const activeStopwatch = allStopwatches.find((stopwatch) => stopwatch.started_on > 0);
  return activeStopwatch || null;
});

export const isLimitedAccess = createSelector(
  getAllProjects,
  getAllTasks,
  (state, projectId, parentType) => parentType,
  (state, projectId, parentType, parentId) => parentId,
  makeCanViewProjectElement(),
  (state) => getRawConfigOption(state, "task_time_tracking_enabled"),
  (projects, tasks, parentType, parentId, canViewProjectElement, isTaskTimeTrackingFeatureEnabled) => {
    if (parentType === userClass) {
      return false;
    }

    if (parentType === taskClass && !isTaskTimeTrackingFeatureEnabled) {
      return true;
    }

    const parent = parentType === taskClass ? tasks.get(`${parentId}`) : projects.get(`${parentId}`);

    if (!parent || parent?.is_trashed) {
      return true;
    }

    return !canViewProjectElement;
  }
);
